/**
 * Utilities Industry Configuration
 *
 * Supports: Substations, Transformers, Circuit Breakers, Transmission Lines, Smart Meters
 */

import { IndustryConfig } from '../industryTypes'

export const utilitiesConfig: IndustryConfig = {
  id: 'utilities',
  name: 'Utilities',
  description: 'Electric power transmission and distribution infrastructure',
  icon: 'Plug',

  terminology: {
    asset: 'Equipment',
    assets: 'Equipment',
    assetPlural: 'Equipment',
    assetType: 'Equipment Type',
    failure: 'Outage',
    health: 'Condition',
    healthScore: 'Health Index',
    healthy: 'Energized',
    degraded: 'Degraded',
    critical: 'De-energized',
    alert: 'Alarm',
    process: 'Transmission',
    maintenance: 'Maintenance',
    inspection: 'Inspection',
    downtime: 'Outage Duration',
    workOrder: 'Work Order',
    prediction: 'Forecast',
    anomaly: 'Disturbance',
    trend: 'Trend',
    risk: 'Risk',
    facility: 'Substation',
    facilityPlural: 'Substations',
    location: 'Feeder',
    operator: 'System Operator',
    supervisor: 'Grid Operations Manager',
    calibration: 'Calibration',
    compliance: 'NERC Compliance',
    criticality: 'Criticality',
  },

  assetTypes: [
    {
      id: 'substation_transformer',
      name: 'Substation Transformer',
      icon: 'Boxes',
      description: 'Power transformers at substations',
      category: 'Substation',
      defaultMetrics: ['oil_temperature', 'winding_temperature', 'dissolved_gas', 'load_tap_changer_position'],
      maintenanceTypes: ['oil_analysis', 'thermography', 'bushing_test', 'tap_changer_service'],
      criticalityLevels: [
        { id: 'critical', name: 'Transmission', color: '#ef4444', description: 'Transmission level transformer', downtimeCostMultiplier: 1000 },
        { id: 'high', name: 'Sub-transmission', color: '#f59e0b', description: 'Sub-transmission transformer', downtimeCostMultiplier: 500 },
        { id: 'medium', name: 'Distribution', color: '#3b82f6', description: 'Distribution transformer', downtimeCostMultiplier: 100 },
        { id: 'low', name: 'Service', color: '#6b7280', description: 'Service transformer', downtimeCostMultiplier: 20 },
      ],
    },
    {
      id: 'circuit_breaker',
      name: 'Circuit Breaker',
      icon: 'ToggleRight',
      description: 'High voltage circuit breakers',
      category: 'Protection',
      defaultMetrics: ['operation_count', 'contact_wear', 'sf6_pressure', 'mechanism_timing'],
      maintenanceTypes: ['timing_test', 'contact_inspection', 'sf6_service', 'mechanism_overhaul'],
      criticalityLevels: [
        { id: 'critical', name: 'Bus Tie', color: '#ef4444', description: 'Bus tie breaker', downtimeCostMultiplier: 500 },
        { id: 'high', name: 'Line', color: '#f59e0b', description: 'Line breaker', downtimeCostMultiplier: 200 },
        { id: 'medium', name: 'Feeder', color: '#3b82f6', description: 'Feeder breaker', downtimeCostMultiplier: 50 },
        { id: 'low', name: 'Capacitor', color: '#6b7280', description: 'Capacitor bank breaker', downtimeCostMultiplier: 20 },
      ],
    },
    {
      id: 'transmission_line',
      name: 'Transmission Line',
      icon: 'Activity',
      description: 'High voltage transmission lines',
      category: 'Transmission',
      defaultMetrics: ['conductor_temperature', 'sag', 'vibration', 'ice_loading'],
      maintenanceTypes: ['line_patrol', 'vegetation_management', 'insulator_inspection', 'conductor_splice'],
      criticalityLevels: [
        { id: 'critical', name: '500kV+', color: '#ef4444', description: 'EHV transmission', downtimeCostMultiplier: 2000 },
        { id: 'high', name: '230kV', color: '#f59e0b', description: 'HV transmission', downtimeCostMultiplier: 1000 },
        { id: 'medium', name: '115kV', color: '#3b82f6', description: 'Transmission', downtimeCostMultiplier: 500 },
        { id: 'low', name: '69kV', color: '#6b7280', description: 'Sub-transmission', downtimeCostMultiplier: 200 },
      ],
    },
    {
      id: 'protective_relay',
      name: 'Protective Relay',
      icon: 'Shield',
      description: 'Digital protective relays',
      category: 'Protection',
      defaultMetrics: ['relay_operations', 'settings_match', 'communication_status', 'battery_voltage'],
      maintenanceTypes: ['relay_testing', 'settings_verification', 'firmware_update', 'calibration'],
      criticalityLevels: [
        { id: 'critical', name: 'Primary', color: '#ef4444', description: 'Primary protection', downtimeCostMultiplier: 300 },
        { id: 'high', name: 'Backup', color: '#f59e0b', description: 'Backup protection', downtimeCostMultiplier: 150 },
        { id: 'medium', name: 'Monitoring', color: '#3b82f6', description: 'Monitoring relay', downtimeCostMultiplier: 50 },
        { id: 'low', name: 'Alarm', color: '#6b7280', description: 'Alarm only', downtimeCostMultiplier: 10 },
      ],
    },
    {
      id: 'smart_meter',
      name: 'Smart Meter',
      icon: 'Gauge',
      description: 'Advanced metering infrastructure',
      category: 'Metering',
      defaultMetrics: ['communication_success', 'voltage_quality', 'outage_detection', 'tamper_alerts'],
      maintenanceTypes: ['meter_testing', 'communication_check', 'firmware_update', 'replacement'],
      criticalityLevels: [
        { id: 'critical', name: 'Industrial', color: '#ef4444', description: 'Industrial customer', downtimeCostMultiplier: 50 },
        { id: 'high', name: 'Commercial', color: '#f59e0b', description: 'Commercial customer', downtimeCostMultiplier: 20 },
        { id: 'medium', name: 'Residential', color: '#3b82f6', description: 'Residential customer', downtimeCostMultiplier: 5 },
        { id: 'low', name: 'Test', color: '#6b7280', description: 'Test meter', downtimeCostMultiplier: 1 },
      ],
    },
  ],

  kpiDomains: [
    {
      id: 'grid_reliability',
      name: 'Grid Reliability',
      icon: 'Activity',
      description: 'Power delivery reliability metrics',
      businessValue: 'Minimize customer outages and improve service quality',
      category: 'universal',
      metrics: [
        { id: 'saidi', name: 'SAIDI', unit: 'minutes', description: 'System Average Interruption Duration Index', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 60, warning: 90, critical: 120 } },
        { id: 'saifi', name: 'SAIFI', unit: 'interruptions', description: 'System Average Interruption Frequency Index', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 1.0, warning: 1.5, critical: 2.0 } },
        { id: 'caidi', name: 'CAIDI', unit: 'minutes', description: 'Customer Average Interruption Duration Index', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 60, warning: 90, critical: 120 } },
      ],
      thresholds: { healthy: { max: 60 }, warning: { min: 60, max: 120 }, critical: { min: 120 } },
      visualizations: ['gauge', 'line_chart', 'comparison', 'trend'],
    },
    {
      id: 'asset_health',
      name: 'Asset Health',
      icon: 'Heart',
      description: 'Equipment condition metrics',
      businessValue: 'Extend asset life and prevent failures',
      category: 'universal',
      metrics: [
        { id: 'health_index', name: 'Health Index', unit: '%', description: 'Overall asset condition', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 80, warning: 60, critical: 40 } },
        { id: 'remaining_life', name: 'Remaining Life', unit: 'years', description: 'Estimated remaining useful life', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 15, warning: 10, critical: 5 } },
        { id: 'failure_probability', name: 'Failure Probability', unit: '%', description: 'Annual failure probability', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 2, warning: 5, critical: 10 } },
      ],
      thresholds: { healthy: { min: 80 }, warning: { min: 50, max: 80 }, critical: { max: 50 } },
      visualizations: ['gauge', 'bar_chart', 'heatmap', 'trend'],
    },
    {
      id: 'power_quality',
      name: 'Power Quality',
      icon: 'Zap',
      description: 'Power delivery quality metrics',
      businessValue: 'Ensure power meets quality standards',
      category: 'industry_specific',
      metrics: [
        { id: 'voltage_deviation', name: 'Voltage Deviation', unit: '%', description: 'Deviation from nominal voltage', dataType: 'continuous', aggregation: 'avg', goodDirection: 'target', defaultThresholds: { healthy: 2, warning: 5, critical: 8 } },
        { id: 'power_factor', name: 'Power Factor', unit: '', description: 'System power factor', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 0.95, warning: 0.90, critical: 0.85 } },
        { id: 'thd', name: 'Total Harmonic Distortion', unit: '%', description: 'Harmonic content', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 3, warning: 5, critical: 8 } },
      ],
      thresholds: { healthy: { max: 2 }, warning: { min: 2, max: 5 }, critical: { min: 5 } },
      visualizations: ['line_chart', 'gauge', 'distribution', 'trend'],
    },
    {
      id: 'nerc_compliance',
      name: 'NERC Compliance',
      icon: 'Shield',
      description: 'Regulatory compliance metrics',
      businessValue: 'Avoid violations and maintain grid reliability',
      category: 'industry_specific',
      metrics: [
        { id: 'compliance_rate', name: 'Compliance Rate', unit: '%', description: 'Standards compliance percentage', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 100, warning: 98, critical: 95 } },
        { id: 'open_violations', name: 'Open Violations', unit: 'count', description: 'Unresolved violations', dataType: 'discrete', aggregation: 'sum', goodDirection: 'lower', defaultThresholds: { healthy: 0, warning: 1, critical: 3 } },
        { id: 'audit_findings', name: 'Audit Findings', unit: 'count', description: 'Recent audit findings', dataType: 'discrete', aggregation: 'sum', goodDirection: 'lower', defaultThresholds: { healthy: 0, warning: 2, critical: 5 } },
      ],
      thresholds: { healthy: { min: 100 }, warning: { min: 95, max: 100 }, critical: { max: 95 } },
      visualizations: ['gauge', 'bar_chart', 'trend', 'table'],
    },
  ],

  maintenanceTypes: [
    {
      id: 'oil_analysis',
      name: 'Transformer Oil Analysis',
      description: 'Dissolved gas analysis and oil testing',
      category: 'condition_based',
      defaultInterval: { value: 6, unit: 'months' },
      laborRequirements: [
        { role: 'Substation Technician', count: 1, hourlyRate: 55 },
      ],
      estimatedDuration: { min: 30, typical: 60, max: 120, unit: 'minutes' },
      skillsRequired: ['sampling', 'electrical_safety'],
      partsCommonly: ['oil_sample_kits', 'ppe'],
    },
    {
      id: 'relay_testing',
      name: 'Protective Relay Testing',
      description: 'Functional testing of protective relays',
      category: 'preventive',
      defaultInterval: { value: 4, unit: 'years' },
      laborRequirements: [
        { role: 'Relay Technician', count: 2, hourlyRate: 75 },
      ],
      estimatedDuration: { min: 4, typical: 8, max: 16, unit: 'hours' },
      skillsRequired: ['relay_testing', 'protection_systems'],
      partsCommonly: ['test_equipment', 'calibration_records'],
    },
    {
      id: 'breaker_timing',
      name: 'Circuit Breaker Timing Test',
      description: 'Timing and contact analysis',
      category: 'preventive',
      defaultInterval: { value: 6, unit: 'years' },
      laborRequirements: [
        { role: 'HV Technician', count: 2, hourlyRate: 70 },
      ],
      estimatedDuration: { min: 2, typical: 4, max: 8, unit: 'hours' },
      skillsRequired: ['breaker_maintenance', 'high_voltage_safety'],
      partsCommonly: ['timing_equipment', 'contact_lubricant'],
    },
  ],

  riskFactors: [
    { id: 'outage_risk', name: 'Outage Risk', description: 'Risk of unplanned outage', category: 'operational', weight: 0.35, calculationMethod: 'exponential', inputs: ['health_index', 'age', 'loading'] },
    { id: 'safety_risk', name: 'Safety Risk', description: 'Risk to personnel and public', category: 'safety', weight: 0.30, calculationMethod: 'threshold', inputs: ['condition', 'proximity', 'exposure'] },
    { id: 'compliance_risk', name: 'Compliance Risk', description: 'Risk of NERC violation', category: 'compliance', weight: 0.20, calculationMethod: 'linear', inputs: ['test_results', 'documentation'] },
    { id: 'financial_risk', name: 'Financial Risk', description: 'Risk of major repair costs', category: 'financial', weight: 0.15, calculationMethod: 'linear', inputs: ['age', 'condition', 'replacement_cost'] },
  ],

  complianceStandards: [
    {
      id: 'nerc_fac',
      name: 'NERC FAC Standards',
      shortName: 'FAC',
      description: 'Facility design and connection requirements',
      authority: 'NERC',
      region: ['US', 'Canada'],
      requirements: [
        { id: 'fac_001', name: 'Facility Ratings', description: 'Accurate facility ratings', kpiId: 'ratings_current', threshold: 100, operator: 'eq', severity: 'critical' },
        { id: 'fac_008', name: 'Facility Ratings Methodology', description: 'Documented methodology', kpiId: 'methodology_documented', threshold: 100, operator: 'eq', severity: 'required' },
      ],
      reportingFrequency: 'quarterly',
    },
    {
      id: 'nerc_prc',
      name: 'NERC PRC Standards',
      shortName: 'PRC',
      description: 'Protection and control standards',
      authority: 'NERC',
      region: ['US', 'Canada'],
      requirements: [
        { id: 'prc_005', name: 'Protection System Maintenance', description: 'Maintenance and testing', kpiId: 'protection_tested', threshold: 100, operator: 'eq', severity: 'critical' },
        { id: 'prc_019', name: 'Coordination', description: 'Coordination of protection', kpiId: 'coordination_verified', threshold: 100, operator: 'eq', severity: 'required' },
      ],
      reportingFrequency: 'annual',
    },
  ],

  visualization: {
    primaryColor: '#8b5cf6',
    secondaryColor: '#10b981',
    healthyColor: '#22c55e',
    warningColor: '#f59e0b',
    criticalColor: '#ef4444',
    chartDefaults: {
      showGrid: true,
      showLegend: true,
      animationDuration: 300,
    },
  },

  narrativeTemplates: [
    {
      id: 'health_summary',
      name: 'Asset Health Summary',
      context: 'asset_health',
      audience: ['manager', 'engineer', 'technician'],
      template: 'Your {assetType} "{assetName}" at {facility} is currently {healthStatus}. Health Index: {healthIndex}%. {trendDescription} {recommendationText}',
      variables: [
        { name: 'assetType', type: 'terminology' },
        { name: 'assetName', type: 'asset' },
        { name: 'facility', type: 'terminology' },
        { name: 'healthStatus', type: 'terminology' },
        { name: 'healthIndex', type: 'percentage' },
        { name: 'trendDescription', type: 'metric' },
        { name: 'recommendationText', type: 'metric' },
      ],
    },
  ],

  features: {
    enableBenchmarking: true,
    enableWhatIf: true,
    enableCollaboration: true,
    enableCompliance: true,
    enableKnowledgeCapture: true,
  },

  defaults: {
    timeRange: '24h',
    refreshInterval: 30,
    defaultDashboard: 'grid-operations',
  },

  professionalRoles: [
    {
      id: 'system_operator',
      category: 'sme' as const,
      name: 'System Operator',
      shortName: 'Sys Op',
      description: 'Monitors and controls grid operations',
      responsibilities: ['Real-time grid monitoring', 'Switching operations', 'Outage restoration'],
      focusAreas: ['Grid reliability', 'Switching safety', 'NERC compliance'],
      explanationStyle: {
        depth: 'technical' as const,
        includeMetrics: true,
        includeCosts: false,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'technical' as const,
        focusOn: ['technical_details', 'safety', 'action_items'] as const,
      },
      decisionAuthority: ['Switching operations', 'Load shedding'],
      reportingTo: 'Grid Operations Manager',
      icon: 'Activity',
    },
    {
      id: 'protection_control_engineer',
      category: 'sme' as const,
      name: 'Protection & Control Engineer',
      shortName: 'P&C Eng',
      description: 'Designs, coordinates, and maintains protection and control systems for transmission and distribution networks',
      responsibilities: [
        'Relay coordination studies and settings',
        'Protection scheme design and review',
        'Fault analysis and event investigation',
        'SCADA and control system integration',
        'NERC PRC standards compliance',
      ],
      focusAreas: ['Protection reliability', 'Relay coordination', 'NERC PRC compliance', 'fault_analysis', 'control_systems'],
      explanationStyle: {
        depth: 'technical' as const,
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'technical' as const,
        focusOn: ['technical_details', 'compliance', 'risk'] as const,
      },
      decisionAuthority: ['Protection settings', 'Relay replacements', 'Control scheme modifications'],
      reportingTo: 'Engineering Manager',
      icon: 'Shield',
    },
    // Operations Roles
    {
      id: 'grid_operations_manager',
      category: 'executive' as const,
      name: 'Grid Operations Manager',
      shortName: 'Grid Ops Mgr',
      description: 'Manages real-time grid operations center, dispatching, and outage restoration for the utility service territory',
      responsibilities: [
        'Real-time grid reliability oversight',
        'Outage management and restoration coordination',
        'System operator team leadership',
        'Emergency operations planning',
        'Regulatory reporting for grid events',
      ],
      focusAreas: ['SAIDI', 'SAIFI', 'outage_duration', 'crew_utilization', 'customer_minutes_interrupted'],
      explanationStyle: {
        depth: 'detailed' as const,
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'professional' as const,
        focusOn: ['business_impact', 'action_items', 'risk'] as const,
      },
      decisionAuthority: ['Load shedding authorization', 'Switching priority', 'Crew dispatch', 'Mutual aid requests'],
      reportingTo: 'VP of Operations',
      icon: 'Activity',
    },
    // Technician Roles
    {
      id: 'line_substation_technician',
      category: 'technician' as const,
      name: 'Line/Substation Technician',
      shortName: 'Line Tech',
      description: 'Performs hands-on installation, maintenance, and repair of overhead lines, underground cables, and substation equipment',
      responsibilities: [
        'Transformer and breaker maintenance execution',
        'Overhead line and cable repairs',
        'Substation equipment testing and inspection',
        'Switching operations under clearance',
        'Storm restoration and emergency repairs',
      ],
      focusAreas: ['equipment_condition', 'safety_procedures', 'switching_orders', 'test_results', 'parts_needed'],
      explanationStyle: {
        depth: 'technical' as const,
        includeMetrics: false,
        includeCosts: false,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'technical' as const,
        focusOn: ['technical_details', 'action_items'] as const,
      },
      decisionAuthority: ['Repair methods', 'Parts selection', 'Immediate safety stops', 'Field switching execution'],
      reportingTo: 'Substation Supervisor',
      icon: 'Wrench',
    },
    // Analyst Roles
    {
      id: 'distribution_planning_engineer',
      category: 'analyst' as const,
      name: 'Distribution Planning Engineer',
      shortName: 'Dist Plan Eng',
      description: 'Plans distribution system capacity, reliability improvements, and capital investment programs using load forecasting and power flow analysis',
      responsibilities: [
        'Distribution system load forecasting',
        'Power flow and short circuit analysis',
        'Capital project planning and prioritization',
        'DER interconnection impact studies',
        'Reliability improvement program development',
      ],
      focusAreas: ['load_growth', 'system_capacity', 'voltage_profiles', 'loss_reduction', 'reliability_indices'],
      explanationStyle: {
        depth: 'detailed' as const,
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: false,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'professional' as const,
        focusOn: ['technical_details', 'business_impact', 'risk'] as const,
      },
      decisionAuthority: ['Capital project recommendations', 'System reconfiguration proposals', 'DER interconnection approvals'],
      reportingTo: 'Director of Engineering',
      icon: 'Map',
    },
    // Compliance Roles
    {
      id: 'nerc_compliance_specialist',
      category: 'analyst' as const,
      name: 'NERC Compliance Specialist',
      shortName: 'NERC Comp',
      description: 'Manages the utility\'s compliance posture with NERC Critical Infrastructure Protection (CIP), Transmission Planning (TPL), and Protection & Control (PRC) reliability standards',
      responsibilities: [
        'NERC standards monitoring and self-assessment',
        'Evidence management and audit preparation',
        'Compliance violation investigation and mitigation',
        'Internal compliance training programs',
        'Regulatory filing and reporting',
      ],
      focusAreas: ['compliance_rate', 'open_violations', 'audit_findings', 'mitigation_plans', 'regulatory_deadlines'],
      explanationStyle: {
        depth: 'detailed' as const,
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'professional' as const,
        focusOn: ['compliance', 'risk', 'action_items'] as const,
      },
      decisionAuthority: ['Self-report decisions', 'Mitigation plan scope', 'Compliance program priorities'],
      reportingTo: 'Chief Compliance Officer',
      icon: 'FileCheck',
    },
  ],

  partsCatalog: {
    categories: [
      {
        id: 'transformer_parts',
        name: 'Transformer Components',
        description: 'Bushings, oil, windings, and tap changer parts for power transformers',
        parts: [
          {
            id: 'BUSH-HV-001',
            partNumber: 'BUSH-HV-001',
            name: 'HV Bushing (245 kV)',
            description: 'High voltage condenser-type transformer bushing, oil-impregnated paper',
            category: 'transformer_parts',
            manufacturer: 'ABB',
            unitCost: 45000,
            currency: 'USD',
            leadTime: { value: 16, unit: 'weeks' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['substation_transformer'],
            failureModes: ['bushing_failure', 'oil_leak', 'partial_discharge'],
            averageLifespan: { value: 30, unit: 'years' as const },
            specifications: { voltageClass: '245 kV', bil: '1050 kV', type: 'OIP' },
          },
          {
            id: 'BUSH-LV-001',
            partNumber: 'BUSH-LV-001',
            name: 'LV Bushing (25 kV)',
            description: 'Low voltage porcelain transformer bushing for distribution class',
            category: 'transformer_parts',
            manufacturer: 'ABB',
            unitCost: 8500,
            currency: 'USD',
            leadTime: { value: 8, unit: 'weeks' as const },
            criticalityLevel: 'essential' as const,
            compatibleAssetTypes: ['substation_transformer'],
            failureModes: ['bushing_failure', 'porcelain_crack'],
            averageLifespan: { value: 35, unit: 'years' as const },
            specifications: { voltageClass: '25 kV', bil: '150 kV', type: 'Porcelain' },
          },
          {
            id: 'XFMR-OIL-001',
            partNumber: 'NYNAS-NYTRO-10XN',
            name: 'Transformer Insulating Oil (55 gal)',
            description: 'Inhibited naphthenic mineral insulating oil, IEC 60296 compliant',
            category: 'transformer_parts',
            manufacturer: 'Nynas',
            unitCost: 1200,
            currency: 'USD',
            leadTime: { value: 1, unit: 'weeks' as const },
            criticalityLevel: 'essential' as const,
            compatibleAssetTypes: ['substation_transformer'],
            failureModes: ['insulation_breakdown', 'overheating'],
            specifications: { volume: '55 gal', standard: 'IEC 60296', type: 'Naphthenic' },
          },
          {
            id: 'XFMR-WINDING-001',
            partNumber: 'WINDING-CTC-001',
            name: 'Continuously Transposed Conductor (CTC)',
            description: 'Copper CTC winding conductor for transformer rewind, epoxy-bonded',
            category: 'transformer_parts',
            manufacturer: 'Essex Furukawa',
            unitCost: 85000,
            currency: 'USD',
            leadTime: { value: 20, unit: 'weeks' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['substation_transformer'],
            failureModes: ['winding_failure', 'insulation_breakdown'],
            averageLifespan: { value: 35, unit: 'years' as const },
            specifications: { material: 'Copper', type: 'CTC', insulation: 'Epoxy-bonded' },
          },
          {
            id: 'XFMR-LTC-001',
            partNumber: 'MR-TYPE-M-CONTACTS',
            name: 'Load Tap Changer Contact Set',
            description: 'Complete arcing and transition contact set for MR Type M on-load tap changer',
            category: 'transformer_parts',
            manufacturer: 'Reinhausen',
            unitCost: 12000,
            currency: 'USD',
            leadTime: { value: 10, unit: 'weeks' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['substation_transformer'],
            failureModes: ['tap_changer_failure', 'contact_wear', 'coking'],
            averageLifespan: { value: 100000, unit: 'cycles' as const },
            specifications: { type: 'Arcing + Transition', ltcModel: 'MR Type M' },
          },
        ],
      },
      {
        id: 'breaker_parts',
        name: 'Circuit Breaker Components',
        description: 'Contacts, SF6 gas, and operating mechanisms for high voltage circuit breakers',
        parts: [
          {
            id: 'BKR-CONTACT-001',
            partNumber: 'ABB-CONTACT-SET-145',
            name: 'Breaker Arcing Contact Set (145 kV)',
            description: 'Main and arcing contact assembly for SF6 circuit breaker interrupter',
            category: 'breaker_parts',
            manufacturer: 'ABB',
            unitCost: 18000,
            currency: 'USD',
            leadTime: { value: 12, unit: 'weeks' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['circuit_breaker'],
            failureModes: ['contact_wear', 'arcing_damage', 'timing_drift'],
            averageLifespan: { value: 5000, unit: 'cycles' as const },
            specifications: { voltageClass: '145 kV', type: 'Arcing + Main' },
          },
          {
            id: 'BKR-SF6-001',
            partNumber: 'SF6-GAS-50LB',
            name: 'SF6 Insulating Gas (50 lb cylinder)',
            description: 'Sulfur hexafluoride gas, ultra-high purity for HV breakers and GIS',
            category: 'breaker_parts',
            manufacturer: 'Concorde Specialty Gases',
            unitCost: 1800,
            currency: 'USD',
            leadTime: { value: 2, unit: 'weeks' as const },
            criticalityLevel: 'essential' as const,
            compatibleAssetTypes: ['circuit_breaker'],
            failureModes: ['sf6_leak', 'dielectric_failure'],
            specifications: { purity: '99.99%', weight: '50 lb', standard: 'IEC 60376' },
          },
          {
            id: 'BKR-MECH-001',
            partNumber: 'ABB-BLG-SPRING-MECH',
            name: 'Spring Operating Mechanism Assembly',
            description: 'Complete spring-charged operating mechanism for HV SF6 breaker',
            category: 'breaker_parts',
            manufacturer: 'ABB',
            unitCost: 28000,
            currency: 'USD',
            leadTime: { value: 14, unit: 'weeks' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['circuit_breaker'],
            failureModes: ['mechanism_failure', 'spring_fatigue', 'timing_drift'],
            averageLifespan: { value: 10000, unit: 'cycles' as const },
            specifications: { type: 'Spring-charged', operatingEnergy: '3600 J' },
          },
        ],
      },
      {
        id: 'line_hardware',
        name: 'Transmission Line Hardware',
        description: 'Insulators, conductors, splice kits, and tower hardware for overhead lines',
        parts: [
          {
            id: 'LINE-INS-001',
            partNumber: 'NGK-SC-DISC-15KV',
            name: 'Porcelain Disc Insulator (15 kV)',
            description: 'Standard porcelain suspension disc insulator, ball-and-socket, ANSI 52-3',
            category: 'line_hardware',
            manufacturer: 'NGK',
            unitCost: 45,
            currency: 'USD',
            leadTime: { value: 4, unit: 'weeks' as const },
            criticalityLevel: 'essential' as const,
            compatibleAssetTypes: ['transmission_line'],
            failureModes: ['insulator_flashover', 'porcelain_crack', 'contamination'],
            averageLifespan: { value: 40, unit: 'years' as const },
            specifications: { type: 'Suspension disc', standard: 'ANSI 52-3', material: 'Porcelain' },
          },
          {
            id: 'LINE-COND-001',
            partNumber: 'ACSR-DRAKE-795',
            name: 'ACSR Conductor Drake 795 kcmil',
            description: 'Aluminum conductor steel reinforced, 795 kcmil, 26/7 stranding (per 1000 ft)',
            category: 'line_hardware',
            manufacturer: 'Southwire',
            unitCost: 2800,
            currency: 'USD',
            leadTime: { value: 6, unit: 'weeks' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['transmission_line'],
            failureModes: ['conductor_break', 'corrosion', 'galloping'],
            averageLifespan: { value: 50, unit: 'years' as const },
            specifications: { type: 'ACSR', size: '795 kcmil', stranding: '26/7', ratedAmpacity: '907 A' },
          },
          {
            id: 'LINE-SPLICE-001',
            partNumber: 'BURNDY-YDS-34',
            name: 'Full Tension Compression Splice Kit',
            description: 'Full tension compression splice for 795 kcmil ACSR conductor, includes dies',
            category: 'line_hardware',
            manufacturer: 'Burndy',
            unitCost: 350,
            currency: 'USD',
            leadTime: { value: 1, unit: 'weeks' as const },
            criticalityLevel: 'essential' as const,
            compatibleAssetTypes: ['transmission_line'],
            failureModes: ['splice_failure', 'hot_spot'],
            averageLifespan: { value: 40, unit: 'years' as const },
            specifications: { type: 'Full tension compression', conductorSize: '795 kcmil' },
          },
          {
            id: 'LINE-TWR-001',
            partNumber: 'TOWER-BOLT-SET-GRD5',
            name: 'Tower Bolt Hardware Kit (Grade 5)',
            description: 'Galvanized bolt, nut, and washer kit for steel lattice tower leg assembly',
            category: 'line_hardware',
            manufacturer: 'Hubbell Power',
            unitCost: 125,
            currency: 'USD',
            leadTime: { value: 1, unit: 'weeks' as const },
            criticalityLevel: 'standard' as const,
            compatibleAssetTypes: ['transmission_line'],
            failureModes: ['corrosion', 'structural_failure'],
            averageLifespan: { value: 30, unit: 'years' as const },
            specifications: { grade: 'Grade 5', finish: 'Hot-dip galvanized', type: 'Bolt/Nut/Washer kit' },
          },
        ],
      },
      {
        id: 'protection_parts',
        name: 'Protection & Station Battery Equipment',
        description: 'Protection relays, station battery cells, and DC chargers',
        parts: [
          {
            id: 'RELAY-SEL-421',
            partNumber: 'SEL-421',
            name: 'Line Distance Protection Relay',
            description: 'High-speed distance relay with synchrophasor measurement, 5A/1A CT inputs',
            category: 'protection_parts',
            manufacturer: 'SEL',
            unitCost: 8500,
            currency: 'USD',
            leadTime: { value: 6, unit: 'weeks' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['protective_relay'],
            failureModes: ['relay_failure', 'communication_loss', 'settings_error'],
            averageLifespan: { value: 20, unit: 'years' as const },
            specifications: { type: 'Distance', ctInput: '5A/1A', protocol: 'IEC 61850 / SEL Mirrored Bits' },
          },
          {
            id: 'RELAY-SEL-787',
            partNumber: 'SEL-787',
            name: 'Transformer Differential Relay',
            description: 'Transformer differential protection relay with harmonic restraint and CT saturation detection',
            category: 'protection_parts',
            manufacturer: 'SEL',
            unitCost: 9200,
            currency: 'USD',
            leadTime: { value: 6, unit: 'weeks' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['protective_relay', 'substation_transformer'],
            failureModes: ['relay_failure', 'misoperation', 'communication_loss'],
            averageLifespan: { value: 20, unit: 'years' as const },
            specifications: { type: 'Differential', ctInputs: '6 restraint + 1 operating', harmonicRestraint: '2nd & 4th' },
          },
          {
            id: 'BATT-CELL-001',
            partNumber: 'EH-GNB-ABSOLYTE-IIP',
            name: 'Station Battery Cell (VRLA 2V 1680Ah)',
            description: 'Valve-regulated lead-acid battery cell for substation DC supply, 2V nominal',
            category: 'protection_parts',
            manufacturer: 'Enersys',
            unitCost: 950,
            currency: 'USD',
            leadTime: { value: 8, unit: 'weeks' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['protective_relay', 'circuit_breaker'],
            failureModes: ['battery_capacity_loss', 'cell_short', 'dry_out'],
            averageLifespan: { value: 15, unit: 'years' as const },
            specifications: { type: 'VRLA', voltage: '2V', capacity: '1680Ah', standard: 'IEEE 485' },
          },
          {
            id: 'DC-CHARGER-001',
            partNumber: 'RECT-125VDC-50A',
            name: 'Station Battery Charger (125 VDC / 50A)',
            description: 'Silicon-controlled rectifier battery charger with equalize mode and float/boost regulation',
            category: 'protection_parts',
            manufacturer: 'Power-One',
            unitCost: 6500,
            currency: 'USD',
            leadTime: { value: 10, unit: 'weeks' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['protective_relay', 'circuit_breaker'],
            failureModes: ['charger_failure', 'regulation_drift', 'rectifier_failure'],
            averageLifespan: { value: 20, unit: 'years' as const },
            specifications: { outputVoltage: '125 VDC', outputCurrent: '50A', type: 'SCR' },
          },
        ],
      },
      {
        id: 'metering_parts',
        name: 'Smart Metering Components',
        description: 'Communication modules and firmware for advanced metering infrastructure',
        parts: [
          {
            id: 'METER-COMM-001',
            partNumber: 'ITRON-OPENWAY-NIC',
            name: 'Smart Meter Communication Module (RF Mesh)',
            description: 'RF mesh network interface card for AMI two-way communication, FCC Part 15',
            category: 'metering_parts',
            manufacturer: 'Itron',
            unitCost: 75,
            currency: 'USD',
            leadTime: { value: 4, unit: 'weeks' as const },
            criticalityLevel: 'essential' as const,
            compatibleAssetTypes: ['smart_meter'],
            failureModes: ['communication_loss', 'firmware_corruption'],
            averageLifespan: { value: 15, unit: 'years' as const },
            specifications: { type: 'RF Mesh', frequency: '900 MHz', protocol: 'OpenWay RIVA' },
          },
          {
            id: 'METER-FW-001',
            partNumber: 'ITRON-FW-CENTRON-V8',
            name: 'Smart Meter Firmware Update Package',
            description: 'Over-the-air firmware update for Centron meters, includes metrology and communications updates',
            category: 'metering_parts',
            manufacturer: 'Itron',
            unitCost: 5,
            currency: 'USD',
            leadTime: { value: 2, unit: 'days' as const },
            criticalityLevel: 'standard' as const,
            compatibleAssetTypes: ['smart_meter'],
            failureModes: ['firmware_corruption', 'communication_loss', 'metering_error'],
            specifications: { type: 'OTA Firmware', version: 'v8.x', meterPlatform: 'Centron' },
          },
        ],
      },
      {
        id: 'capacitor_bank_parts',
        name: 'Capacitor Bank Components',
        description: 'Capacitor cans, fuses, and controllers for power factor correction and voltage support',
        parts: [
          {
            id: 'CAPBANK-CAN-001',
            partNumber: 'COOPER-CYUV-200KVAR',
            name: 'Capacitor Can (200 kVAR)',
            description: 'All-film, internally fused power capacitor can for distribution capacitor banks',
            category: 'capacitor_bank_parts',
            manufacturer: 'Eaton Cooper',
            unitCost: 2200,
            currency: 'USD',
            leadTime: { value: 6, unit: 'weeks' as const },
            criticalityLevel: 'essential' as const,
            compatibleAssetTypes: ['circuit_breaker'],
            failureModes: ['capacitor_failure', 'dielectric_breakdown', 'swelling'],
            averageLifespan: { value: 15, unit: 'years' as const },
            specifications: { rating: '200 kVAR', voltage: '7.2 kV', type: 'All-film internally fused' },
          },
          {
            id: 'CAPBANK-CTRL-001',
            partNumber: 'BECKWITH-M-6200A',
            name: 'Capacitor Bank Controller',
            description: 'Automatic capacitor bank switching controller with voltage, VAR, and time-based control modes',
            category: 'capacitor_bank_parts',
            manufacturer: 'Beckwith Electric',
            unitCost: 4500,
            currency: 'USD',
            leadTime: { value: 6, unit: 'weeks' as const },
            criticalityLevel: 'essential' as const,
            compatibleAssetTypes: ['circuit_breaker'],
            failureModes: ['controller_failure', 'sensor_drift', 'communication_loss'],
            averageLifespan: { value: 20, unit: 'years' as const },
            specifications: { controlModes: 'Voltage/VAR/Time/Temperature', communication: 'DNP3 / Modbus' },
          },
        ],
      },
    ],
  },

  rootCauseLibrary: {
    categories: [
      // Category 1: Insulation Degradation
      {
        id: 'insulation_degradation',
        name: 'Insulation Degradation',
        description: 'Root causes related to insulation breakdown in transformers, cables, and switchgear',
        icon: 'AlertTriangle',
        color: '#ef4444',
        rootCauses: [
          {
            id: 'transformer_insulation_breakdown',
            name: 'Transformer Insulation Breakdown',
            description: 'Progressive degradation of cellulose and oil insulation in power transformers due to thermal aging, moisture ingress, or oxidation',
            category: 'insulation_degradation',
            symptoms: [
              'Elevated dissolved gas levels (H2, C2H2, C2H4)',
              'Hot spots detected on thermography',
              'Partial discharge activity detected',
              'Decreasing oil dielectric strength',
              'Increasing moisture content in oil',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Perform dissolved gas analysis (DGA) on transformer oil',
                expectedResult: 'Identify fault gases: H2 > 100 ppm, C2H2 > 2 ppm, or total combustible gas exceeding IEEE C57.104 limits',
                tools: ['DGA sampling kit', 'Gas chromatograph or lab analysis'],
                duration: 60,
                skillLevel: 'advanced' as const,
              },
              {
                order: 2,
                action: 'Conduct infrared thermography scan of transformer exterior',
                expectedResult: 'Identify abnormal hot spots on tank walls, bushings, or radiators exceeding 10 deg C above baseline',
                tools: ['IR camera', 'Thermography report template'],
                duration: 30,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 3,
                action: 'Perform power factor (tan delta) test on bushings and windings',
                expectedResult: 'Power factor < 1% at 20 deg C for oil-filled bushings; values trending upward indicate degradation',
                tools: ['Doble M4100 or equivalent', 'Test leads'],
                duration: 120,
                skillLevel: 'advanced' as const,
              },
              {
                order: 4,
                action: 'Test oil moisture content and dielectric breakdown voltage',
                expectedResult: 'Moisture < 15 ppm for 230 kV class; BDV > 30 kV per ASTM D1816',
                tools: ['Moisture analyzer', 'BDV test set'],
                duration: 45,
                skillLevel: 'advanced' as const,
              },
            ],
            preventiveActions: [
              'Regular DGA testing per IEEE C57.104 intervals',
              'Maintain oil quality through filtration and degassing',
              'Control loading to limit hot spot temperatures',
              'Inspect and maintain cooling systems (fans, pumps, radiators)',
              'Monitor bushing power factor trend annually',
            ],
            typicalProbability: 0.08,
            affectedComponents: ['windings', 'bushings', 'insulating_oil', 'leads'],
            requiredExpertise: ['sme' as const],
            estimatedResolutionTime: { min: 24, typical: 168, max: 720, unit: 'hours' as const },
            costRange: { min: 50000, max: 2000000, currency: 'USD' },
            industryReferences: ['IEEE C57.104', 'IEEE C57.106', 'IEC 60599'],
          },
          {
            id: 'cable_insulation_failure',
            name: 'Underground Cable Insulation Failure',
            description: 'Breakdown of XLPE or EPR cable insulation due to water treeing, electrical treeing, or manufacturing defects',
            category: 'insulation_degradation',
            symptoms: [
              'Increased partial discharge levels on cable testing',
              'Tan delta values trending upward',
              'Recurring cable faults in same segment',
              'Visible signs of tracking or treeing at terminations',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Perform VLF (Very Low Frequency) tan delta test on cable',
                expectedResult: 'Tan delta < 40e-3 for aged XLPE; increasing trend or high differential indicates degradation',
                tools: ['VLF test set', 'Tan delta measurement unit'],
                duration: 120,
                skillLevel: 'advanced' as const,
              },
              {
                order: 2,
                action: 'Conduct partial discharge mapping using online or offline PD test',
                expectedResult: 'Locate PD sites along cable route; PD levels above background indicate insulation defects',
                tools: ['PD detector', 'Cable route map'],
                duration: 180,
                skillLevel: 'advanced' as const,
              },
            ],
            preventiveActions: [
              'VLF withstand and tan delta testing at commissioning and periodically',
              'Proper cable installation with minimum bend radius adherence',
              'Use moisture-sealed cable terminations and splices',
              'Monitor manhole and duct moisture levels',
            ],
            typicalProbability: 0.06,
            affectedComponents: ['cable_insulation', 'cable_terminations', 'cable_splices'],
            requiredExpertise: ['sme' as const],
            estimatedResolutionTime: { min: 8, typical: 48, max: 240, unit: 'hours' as const },
            costRange: { min: 25000, max: 500000, currency: 'USD' },
            industryReferences: ['IEEE 400', 'IEEE 400.2'],
          },
        ],
      },
      // Category 2: Mechanical Failures
      {
        id: 'mechanical_failures',
        name: 'Mechanical Failures',
        description: 'Root causes related to mechanical components in breakers, tap changers, and switchgear',
        icon: 'Cog',
        color: '#3b82f6',
        rootCauses: [
          {
            id: 'breaker_mechanism_failure',
            name: 'Circuit Breaker Mechanism Failure',
            description: 'Failure of spring-charged or hydraulic operating mechanism causing slow operation, failure to trip, or failure to close',
            category: 'mechanical_failures',
            symptoms: [
              'Slow trip or close times outside manufacturer tolerance',
              'Mechanism charging motor running excessively',
              'Low gas pressure alarms on SF6 breakers',
              'Visible hydraulic oil leaks',
              'Operation counter discrepancies',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Perform breaker timing test (trip, close, trip-close-trip)',
                expectedResult: 'Trip time < 50 ms, close time < 80 ms (typical); compare to manufacturer baseline within ±10%',
                tools: ['Breaker timing analyzer', 'Transducer set'],
                duration: 120,
                skillLevel: 'advanced' as const,
              },
              {
                order: 2,
                action: 'Inspect operating mechanism linkages and springs',
                expectedResult: 'No cracked or corroded linkages; springs at correct preload',
                tools: ['Inspection light', 'Torque wrench'],
                duration: 60,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 3,
                action: 'Measure SF6 gas pressure and check for leaks',
                expectedResult: 'Gas pressure at rated value; leak rate < 0.5% per year per IEC 62271',
                tools: ['SF6 gas analyzer', 'SF6 leak detector'],
                duration: 45,
                skillLevel: 'intermediate' as const,
              },
            ],
            preventiveActions: [
              'Perform timing tests per manufacturer maintenance schedule',
              'Lubricate mechanism linkages per OEM specifications',
              'Monitor SF6 gas pressure with continuous density sensors',
              'Replace mechanism wear parts at recommended operation counts',
              'Maintain operation counter and trend performance',
            ],
            typicalProbability: 0.10,
            affectedComponents: ['operating_mechanism', 'springs', 'linkages', 'sf6_system', 'contacts'],
            requiredExpertise: ['technician' as const],
            estimatedResolutionTime: { min: 4, typical: 16, max: 72, unit: 'hours' as const },
            costRange: { min: 5000, max: 80000, currency: 'USD' },
            industryReferences: ['IEEE C37.10', 'IEC 62271-100'],
          },
          {
            id: 'tap_changer_mechanical',
            name: 'Load Tap Changer Mechanical Wear',
            description: 'Mechanical wear of on-load tap changer contacts, drive shafts, and Geneva mechanisms leading to misoperation or failure to regulate voltage',
            category: 'mechanical_failures',
            symptoms: [
              'Increasing tap change time',
              'Abnormal sounds during tap change operation',
              'Voltage regulation hunting or instability',
              'Elevated DGA gases in LTC compartment oil',
              'Motor drive overcurrent alarms',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Perform dynamic resistance measurement (DRM) during tap change',
                expectedResult: 'Smooth resistance transition without open-circuit breaks; compare to known-good waveforms',
                tools: ['DRM test set', 'Waveform analyzer'],
                duration: 90,
                skillLevel: 'advanced' as const,
              },
              {
                order: 2,
                action: 'Inspect LTC compartment oil via DGA',
                expectedResult: 'Acetylene < 2 ppm; elevated C2H2 indicates arcing contact damage',
                tools: ['DGA sampling kit'],
                duration: 30,
                skillLevel: 'intermediate' as const,
              },
            ],
            preventiveActions: [
              'Replace contact sets at manufacturer-recommended operation count',
              'Regular LTC compartment oil sampling for DGA',
              'Inspect and lubricate drive mechanism per OEM schedule',
              'Monitor tap change time trends',
            ],
            typicalProbability: 0.07,
            affectedComponents: ['ltc_contacts', 'drive_mechanism', 'selector_switch', 'ltc_oil'],
            requiredExpertise: ['technician' as const, 'sme' as const],
            estimatedResolutionTime: { min: 8, typical: 40, max: 120, unit: 'hours' as const },
            costRange: { min: 15000, max: 150000, currency: 'USD' },
            industryReferences: ['IEEE C57.131', 'IEC 60214'],
          },
        ],
      },
      // Category 3: Environmental & Weather Damage
      {
        id: 'environmental_weather',
        name: 'Environmental & Weather Damage',
        description: 'Root causes from lightning, ice storms, windstorms, flooding, and contamination',
        icon: 'CloudLightning',
        color: '#6366f1',
        rootCauses: [
          {
            id: 'lightning_damage',
            name: 'Lightning Strike Damage',
            description: 'Equipment damage from direct lightning strikes or induced surges, affecting transformers, arresters, and line equipment',
            category: 'environmental_weather',
            symptoms: [
              'Surge arrester failure or expulsion',
              'Transformer winding damage following storm',
              'Relay misoperation or lockout during storm',
              'Blown fuses on multiple feeders simultaneously',
              'Visible arc damage on line hardware',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Inspect surge arresters for signs of failure (cracked housing, expulsion)',
                expectedResult: 'All arresters intact; failed units identified for replacement',
                tools: ['Visual inspection', 'Binoculars', 'Arrester leakage current tester'],
                duration: 60,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 2,
                action: 'Perform transformer DGA to detect lightning-induced winding damage',
                expectedResult: 'Elevated H2 or C2H2 indicates internal arcing from surge',
                tools: ['DGA sampling kit'],
                duration: 45,
                skillLevel: 'advanced' as const,
              },
              {
                order: 3,
                action: 'Check grounding resistance at affected structures',
                expectedResult: 'Ground resistance < 25 ohms per NESC; high resistance exacerbates lightning damage',
                tools: ['Ground resistance tester (fall-of-potential method)'],
                duration: 30,
                skillLevel: 'intermediate' as const,
              },
            ],
            preventiveActions: [
              'Install or upgrade surge arresters on all exposed equipment',
              'Improve grounding systems at substations and towers',
              'Install overhead ground wires on transmission lines',
              'Use lightning monitoring systems to track strike density',
              'Perform arrester condition assessment after major storms',
            ],
            typicalProbability: 0.12,
            affectedComponents: ['surge_arresters', 'transformer_windings', 'insulators', 'line_hardware'],
            requiredExpertise: ['technician' as const],
            estimatedResolutionTime: { min: 2, typical: 12, max: 72, unit: 'hours' as const },
            costRange: { min: 1000, max: 250000, currency: 'USD' },
            industryReferences: ['IEEE C62.11', 'NESC Section 9'],
          },
          {
            id: 'ice_wind_damage',
            name: 'Ice & Wind Loading Damage',
            description: 'Structural and conductor damage from ice accumulation and high winds exceeding design loading',
            category: 'environmental_weather',
            symptoms: [
              'Conductor sag below minimum clearance',
              'Tower member buckling or failure',
              'Insulator string breakage',
              'Galloping-induced conductor fatigue',
              'Cross-arm damage from ice loading',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Conduct aerial or ground patrol of affected line sections',
                expectedResult: 'Identify damaged structures, broken conductors, and downed hardware',
                tools: ['Drone', 'Binoculars', 'Patrol vehicle'],
                duration: 240,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 2,
                action: 'Measure conductor sag at mid-span under current loading conditions',
                expectedResult: 'Sag within design limits per ruling span tables; excessive sag indicates damage or elongation',
                tools: ['Clinometer', 'Surveying equipment'],
                duration: 60,
                skillLevel: 'intermediate' as const,
              },
            ],
            preventiveActions: [
              'Design lines to appropriate NESC loading district criteria',
              'Install anti-galloping devices in susceptible areas',
              'Conduct vegetation management to reduce ice-laden tree contact',
              'Use helicopter de-icing where feasible for critical circuits',
              'Maintain emergency restoration structure inventory',
            ],
            typicalProbability: 0.08,
            affectedComponents: ['conductors', 'tower_structures', 'insulators', 'cross_arms', 'guy_wires'],
            requiredExpertise: ['technician' as const, 'sme' as const],
            estimatedResolutionTime: { min: 8, typical: 72, max: 336, unit: 'hours' as const },
            costRange: { min: 10000, max: 1000000, currency: 'USD' },
            industryReferences: ['NESC Rule 250', 'ASCE Manual 74'],
          },
        ],
      },
      // Category 4: Protection System Misoperation
      {
        id: 'protection_misoperation',
        name: 'Protection System Misoperation',
        description: 'Root causes for relay misoperations, incorrect tripping, and failure-to-trip events',
        icon: 'ShieldAlert',
        color: '#f59e0b',
        rootCauses: [
          {
            id: 'relay_settings_error',
            name: 'Relay Settings Error',
            description: 'Incorrect protection relay settings causing uncoordinated tripping, overreach, or underreach during fault conditions',
            category: 'protection_misoperation',
            symptoms: [
              'Breaker trips for faults outside its zone of protection',
              'Failure to trip for in-zone faults (backup relay operates instead)',
              'Protection coordination studies show overlap or gaps',
              'Recurring misoperations on the same feeder or line',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Pull relay event report and oscillography for the misoperation',
                expectedResult: 'Identify which elements operated and compare fault current/voltage to expected reach',
                tools: ['Relay software (AcSELerator, ENERVISTA)', 'Laptop'],
                duration: 30,
                skillLevel: 'advanced' as const,
              },
              {
                order: 2,
                action: 'Perform short circuit study to verify expected fault levels at the relay location',
                expectedResult: 'Modeled fault currents match event data; settings should operate correctly for these levels',
                tools: ['ASPEN OneLiner', 'ETAP', 'or equivalent'],
                duration: 120,
                skillLevel: 'advanced' as const,
              },
              {
                order: 3,
                action: 'Review coordination study and time-current curves for the affected zone',
                expectedResult: 'Adequate coordination margins (0.3s minimum) between upstream and downstream devices',
                tools: ['Coordination software', 'TCC plotting tool'],
                duration: 60,
                skillLevel: 'advanced' as const,
              },
            ],
            preventiveActions: [
              'Validate relay settings against latest short circuit model before commissioning',
              'Perform periodic coordination reviews when system topology changes',
              'Implement peer review process for all relay settings',
              'Use automated settings validation tools',
              'Maintain as-built drawings and settings database',
            ],
            typicalProbability: 0.09,
            affectedComponents: ['protective_relays', 'relay_settings', 'coordination_scheme'],
            requiredExpertise: ['sme' as const],
            estimatedResolutionTime: { min: 4, typical: 24, max: 80, unit: 'hours' as const },
            costRange: { min: 2000, max: 50000, currency: 'USD' },
            industryReferences: ['NERC PRC-004', 'NERC PRC-001', 'IEEE C37.113'],
          },
          {
            id: 'ct_vt_failure',
            name: 'Instrument Transformer (CT/VT) Failure',
            description: 'Current transformer or voltage transformer saturation, open circuit, or ratio error causing relay misoperation',
            category: 'protection_misoperation',
            symptoms: [
              'Relay trips with no apparent fault',
              'Differential relay operates due to CT mismatch',
              'Metering readings inconsistent with SCADA',
              'CT secondary open-circuit arc or burn marks',
              'VT fuse blown causing loss of potential',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Verify CT ratio and polarity with primary injection test',
                expectedResult: 'Measured ratio within nameplate tolerance (typically ±0.3% for metering, ±10% for protection at rated burden)',
                tools: ['Primary injection test set', 'Ammeter'],
                duration: 90,
                skillLevel: 'advanced' as const,
              },
              {
                order: 2,
                action: 'Check CT excitation curve (saturation test)',
                expectedResult: 'Knee-point voltage consistent with manufacturer curve; no indication of shorted turns',
                tools: ['CT analyzer', 'Variable voltage source'],
                duration: 60,
                skillLevel: 'advanced' as const,
              },
              {
                order: 3,
                action: 'Inspect VT fuses and secondary wiring',
                expectedResult: 'All VT fuses intact; secondary voltages balanced and at rated value',
                tools: ['Multimeter', 'Wiring diagrams'],
                duration: 30,
                skillLevel: 'intermediate' as const,
              },
            ],
            preventiveActions: [
              'Select CTs with adequate C-rating for the application burden',
              'Perform excitation tests during commissioning and periodically',
              'Install CT shorting blocks and maintain proper jumper placement',
              'Use VT fuse monitoring relays for loss-of-potential detection',
              'Document actual CT/VT burden vs. rated burden',
            ],
            typicalProbability: 0.05,
            affectedComponents: ['current_transformers', 'voltage_transformers', 'relay_inputs', 'secondary_wiring'],
            requiredExpertise: ['sme' as const, 'technician' as const],
            estimatedResolutionTime: { min: 4, typical: 16, max: 48, unit: 'hours' as const },
            costRange: { min: 3000, max: 75000, currency: 'USD' },
            industryReferences: ['IEEE C57.13', 'IEEE C37.110'],
          },
        ],
      },
      // Category 5: Overloading & Thermal Stress
      {
        id: 'overloading_thermal',
        name: 'Overloading & Thermal Stress',
        description: 'Root causes from sustained or emergency overloading that accelerates aging and causes thermal damage',
        icon: 'Thermometer',
        color: '#dc2626',
        rootCauses: [
          {
            id: 'transformer_overloading',
            name: 'Transformer Overloading',
            description: 'Sustained or emergency loading beyond nameplate rating causing accelerated insulation aging and potential winding damage',
            category: 'overloading_thermal',
            symptoms: [
              'Top oil temperature exceeding alarm setpoint',
              'Winding hot spot temperature above 110 deg C',
              'Cooling fans and pumps running at maximum',
              'Oil level rising above maximum mark',
              'Dissolved gas trends showing thermal fault gases (C2H4, C2H6)',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Review load profile and compare to nameplate rating and IEEE C57.91 loading guides',
                expectedResult: 'Loading should be within normal life expectancy limits (typically 80% of nameplate for continuous, 140% for short-term emergency)',
                tools: ['SCADA historian', 'Loading analysis software'],
                duration: 60,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 2,
                action: 'Check cooling system status (fans, pumps, radiator blockage)',
                expectedResult: 'All cooling stages operational; radiators free of debris and obstruction',
                tools: ['IR camera', 'Visual inspection'],
                duration: 30,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 3,
                action: 'Perform DGA to assess thermal damage from overloading',
                expectedResult: 'C2H4 and C2H6 levels indicating thermal degradation per IEEE C57.104 condition codes',
                tools: ['DGA sampling kit'],
                duration: 45,
                skillLevel: 'advanced' as const,
              },
            ],
            preventiveActions: [
              'Implement real-time loading monitoring with dynamic thermal ratings',
              'Maintain all cooling stages in service during peak seasons',
              'Plan load transfers and contingency switching to reduce loading',
              'Schedule capital replacements for chronically overloaded transformers',
              'Use IEEE C57.91 loading calculations for emergency planning',
            ],
            typicalProbability: 0.11,
            affectedComponents: ['transformer_windings', 'insulating_oil', 'cooling_system', 'bushings'],
            requiredExpertise: ['sme' as const, 'analyst' as const],
            estimatedResolutionTime: { min: 4, typical: 48, max: 168, unit: 'hours' as const },
            costRange: { min: 5000, max: 500000, currency: 'USD' },
            industryReferences: ['IEEE C57.91', 'IEEE C57.104'],
          },
          {
            id: 'conductor_thermal_sag',
            name: 'Conductor Thermal Sag Violation',
            description: 'Excessive conductor heating from overloading causing thermal elongation and sag beyond minimum ground clearance limits',
            category: 'overloading_thermal',
            symptoms: [
              'Line sag monitors showing clearance approaching limits',
              'Conductor temperature exceeding rated continuous temperature',
              'NERC FAC-008 clearance violations reported',
              'Real-time thermal rating alerts',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Review real-time line ratings or sag monitor data',
                expectedResult: 'Clearances meet NESC minimums under actual loading; conductor temperature within design limits',
                tools: ['Line rating system', 'Sag monitors', 'SCADA'],
                duration: 30,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 2,
                action: 'Verify conductor properties (type, stranding, rated temperature)',
                expectedResult: 'Conductor nameplate and ruling span tables align with actual installation',
                tools: ['Line design records', 'PLS-CADD or equivalent'],
                duration: 60,
                skillLevel: 'advanced' as const,
              },
            ],
            preventiveActions: [
              'Install real-time dynamic line rating systems on critical circuits',
              'Reconductor with high-temperature conductor (ACSS, HTLS) where needed',
              'Implement seasonal or ambient-adjusted line ratings',
              'Coordinate with planning to manage load growth on constrained corridors',
            ],
            typicalProbability: 0.06,
            affectedComponents: ['conductors', 'line_clearances', 'tower_structures'],
            requiredExpertise: ['sme' as const, 'analyst' as const],
            estimatedResolutionTime: { min: 2, typical: 24, max: 720, unit: 'hours' as const },
            costRange: { min: 2000, max: 2000000, currency: 'USD' },
            industryReferences: ['NERC FAC-008', 'NESC Rule 232', 'IEEE 738'],
          },
        ],
      },
      // Category 6: Corrosion & Aging
      {
        id: 'corrosion_aging',
        name: 'Corrosion & Aging',
        description: 'Root causes from long-term material degradation, galvanic corrosion, and end-of-life equipment',
        icon: 'Clock',
        color: '#a855f7',
        rootCauses: [
          {
            id: 'steel_structure_corrosion',
            name: 'Steel Structure Corrosion',
            description: 'Galvanic and atmospheric corrosion of lattice towers, substation steel, and anchor systems leading to structural strength reduction',
            category: 'corrosion_aging',
            symptoms: [
              'Visible rust, flaking, or section loss on tower members',
              'Foundation anchor bolt corrosion or grout cracking',
              'Guy wire strand breakage or corrosion pitting',
              'Substation bus support corrosion at base plates',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Conduct visual and tactile inspection of steel members, measuring section loss with ultrasonic thickness gauge',
                expectedResult: 'Remaining wall thickness > 80% of original; no section loss > 20% per ASCE guidelines',
                tools: ['UT thickness gauge', 'Wire brush', 'Inspection mirror'],
                duration: 120,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 2,
                action: 'Evaluate foundation and anchor bolts for corrosion',
                expectedResult: 'Anchor bolts intact with no visible section loss; grout sealed and intact',
                tools: ['Excavation tools', 'Torque wrench', 'Bolt calipers'],
                duration: 90,
                skillLevel: 'intermediate' as const,
              },
            ],
            preventiveActions: [
              'Implement structured corrosion inspection program per ASCE/SEI guidelines',
              'Apply protective coatings or galvanizing touch-up on corroded areas',
              'Replace severely corroded members or foundations',
              'Use sacrificial anodes or cathodic protection on buried steel',
              'Monitor coastal and industrial exposure zones more frequently',
            ],
            typicalProbability: 0.09,
            affectedComponents: ['tower_steel', 'foundation_bolts', 'bus_supports', 'guy_wires'],
            requiredExpertise: ['technician' as const, 'sme' as const],
            estimatedResolutionTime: { min: 8, typical: 80, max: 480, unit: 'hours' as const },
            costRange: { min: 5000, max: 300000, currency: 'USD' },
            industryReferences: ['ASCE Manual 74', 'NACE SP0169'],
          },
          {
            id: 'equipment_end_of_life',
            name: 'Equipment End-of-Life Aging',
            description: 'General age-related degradation where multiple components simultaneously approach or exceed design life, increasing failure probability',
            category: 'corrosion_aging',
            symptoms: [
              'Increasing frequency of forced outages on the asset',
              'Multiple deferred maintenance items accumulating',
              'Condition assessment scores declining below acceptable thresholds',
              'Spare parts becoming obsolete or difficult to source',
              'Health index trending below 40%',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Perform comprehensive condition assessment per IEEE/EPRI guidelines',
                expectedResult: 'Assign health index score based on all test results, visual condition, and operating history',
                tools: ['Condition assessment checklist', 'All relevant test equipment'],
                duration: 480,
                skillLevel: 'advanced' as const,
              },
              {
                order: 2,
                action: 'Conduct replacement priority analysis including risk, consequence, and cost-benefit',
                expectedResult: 'Risk-ranked replacement recommendation with economic justification',
                tools: ['Asset management software', 'Risk model'],
                duration: 240,
                skillLevel: 'advanced' as const,
              },
            ],
            preventiveActions: [
              'Implement asset health indexing and fleet management programs',
              'Develop long-range capital replacement plans based on condition data',
              'Increase monitoring frequency for aging assets',
              'Stock critical spare parts before obsolescence',
              'Plan for technology refresh cycles (25-40 years for transformers, 20-30 for breakers)',
            ],
            typicalProbability: 0.14,
            affectedComponents: ['all_components'],
            requiredExpertise: ['sme' as const, 'analyst' as const],
            estimatedResolutionTime: { min: 720, typical: 2160, max: 8760, unit: 'hours' as const },
            costRange: { min: 100000, max: 5000000, currency: 'USD' },
            industryReferences: ['IEEE C57.140', 'EPRI Asset Management Guidelines', 'CIGRE TB 761'],
          },
        ],
      },
    ],
    commonPatterns: [
      {
        id: 'overload_aging',
        name: 'Overload Aging Pattern',
        description: 'Accelerated aging from chronic overloading leading to insulation degradation and eventual transformer failure',
        sequence: [
          'Sustained loading above nameplate rating',
          'Elevated hot spot and top oil temperatures',
          'Accelerated cellulose decomposition (DP reduction)',
          'Moisture generation from cellulose aging',
          'Oil quality degradation and increased dissolved gases',
          'Insulation failure (winding or bushing)',
        ],
        indicators: ['High load factor (> 0.8)', 'Elevated hot spot temperature', 'Rising CO2/CO ratio in DGA', 'Decreasing oil BDV'],
        preventionStrategy: 'Implement real-time thermal monitoring, optimize cooling, and plan load transfers. Use IEEE C57.91 loading guides to balance loading vs. loss of life.',
      },
      {
        id: 'protection_cascade',
        name: 'Protection System Cascade Failure',
        description: 'A misoperation or hidden failure in protection systems leads to a cascading outage across interconnected zones',
        sequence: [
          'Initial fault occurs on line or equipment',
          'Primary protection fails to operate or misoperates (relay settings error, CT saturation, or communication failure)',
          'Backup protection operates with wider reach, tripping additional elements',
          'Remaining system overloaded due to lost elements',
          'Subsequent protection operations isolate additional load',
          'Widespread customer outage',
        ],
        indicators: ['Untested relay settings in service', 'Disabled backup protection', 'Communication channel failures', 'Overdue relay testing'],
        preventionStrategy: 'Maintain rigorous relay testing schedule per NERC PRC-005. Validate all settings against current short circuit model. Ensure backup protection and communication channels are functional.',
      },
      {
        id: 'corrosion_failure_chain',
        name: 'Corrosion-to-Structural Failure Chain',
        description: 'Progressive corrosion weakens structural members until a loading event causes collapse',
        sequence: [
          'Galvanizing or coating deterioration',
          'Surface rust initiation at connections and base plates',
          'Progressive section loss of steel members',
          'Reduced structural capacity below design loading',
          'Ice, wind, or construction event exceeds weakened capacity',
          'Tower or structure failure',
        ],
        indicators: ['Visible rust or coating failure during patrols', 'Coastal or high-corrosion environment', 'Structures older than 50 years without coating renewal'],
        preventionStrategy: 'Implement drone-based or climbing inspection programs. Prioritize coating renewal and member replacement based on section loss measurements.',
      },
      {
        id: 'thermal_runaway_transformer',
        name: 'Transformer Thermal Runaway',
        description: 'A self-reinforcing cycle where initial hotspot leads to gassing, which further degrades insulation and increases losses',
        sequence: [
          'Localized hot spot from poor oil circulation or winding defect',
          'Gas bubble formation in oil near hot spot',
          'Reduced dielectric strength in gas-affected region',
          'Partial discharge initiation',
          'Increased heating from PD energy dissipation',
          'Catastrophic insulation failure and internal arc',
        ],
        indicators: ['Rapidly increasing DGA gas generation rates', 'Hot spot temperature rising faster than load', 'Partial discharge detected on online PD monitors'],
        preventionStrategy: 'Install online DGA and PD monitoring on critical transformers. Establish rate-of-change alarms for key gases. Maintain cooling system performance.',
      },
    ],
  },

  laborRates: {
    roles: [
      { roleId: 'substation_tech', name: 'Substation Technician', hourlyRate: 55, currency: 'USD', certifications: ['Substation Entry'] },
      { roleId: 'relay_tech', name: 'Relay Technician', hourlyRate: 75, currency: 'USD', certifications: ['NERC Certified'] },
      { roleId: 'protection_engineer', name: 'Protection Engineer', hourlyRate: 110, currency: 'USD', certifications: ['PE', 'NERC'] },
    ],
    overtimeMultiplier: 1.5,
    emergencyMultiplier: 2.0,
    weekendMultiplier: 1.5,
  },
}
